import {
  memo,
  useEffect,
  useState,
  SetStateAction,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { CreativeReport, CreativeReportSection, ReportType } from "../types";
import { getDataConfigurations, updateReportSection } from "../apiService";
import ToastContent from "@/components/common/toast/ToastContent";
import { toast } from "sonner";
import { LuLoaderCircle, LuSettings2 } from "react-icons/lu";
import { useGlobalStore } from "@/GlobalStore";
import { useParams, useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { Button } from "@/components/ui/button";
import {
  CreativeReportSection as SectionComponent,
  CreativeReportSectionRef,
} from "./CreativeReportSection";
import { LabelDescriptionDialogV2 as LabelDescriptionDialog } from "@/screens/creative-label/components/LabelDescriptionDialogV2";
import { CreativeSectionConfiguration, DataConfigurations } from "@/types";
import { useFatigueSettings } from "../hooks/useFatigueSettings";
import { useReportCache } from "../hooks/useReportCache";
import { FatigueSettingsDialog } from "./fatigue/FatigueSettingsDialog";
import { formatRelativeTime } from "@/utils/dateUtils";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";
import PageLoader from "@/components/common/loaders/PageLoader";
import { RiChatSmileAiLine, RiRefreshLine } from "@remixicon/react";
import NoReports from "./empty/NoReports";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ISidebarItem } from "@/components/common/sidebar/SidebarItem";
import { TZDate } from "@date-fns/tz";
import ToastNotification from "@/components/common/toast/ToastNotification";
export interface CreativesReportRef {
  handleSave: () => Promise<void>;
}

type Props = {
  isChatOpen: boolean;
  setIsChatOpen: (isChatOpen: boolean) => void;
  reportConfig: CreativeReport | null;
  setReportConfig: React.Dispatch<SetStateAction<CreativeReport | null>>;
  reportConfigLoading: boolean;
  selectedSection:
    | (CreativeReportSection & { overrideScroll?: boolean; configFresh?: boolean })
    | null;
  setSelectedSection: (
    section:
      | (CreativeReportSection & { overrideScroll?: boolean; configFresh?: boolean })
      | null,
  ) => void;
  handleAddSection: (
    presetConfiguration?: Partial<CreativeSectionConfiguration>,
    title?: string,
  ) => Promise<void>;
  isAddingSection: boolean;
  allowedDateRange: {
    first_allowed_date: TZDate;
    last_allowed_date: TZDate;
  } | null;
  isAccountSynced: boolean;
  hideAddSectionButton?: boolean;
  selectedSidebarItem: ISidebarItem | null;
  isCreatingLabel: boolean;
  setIsCreatingLabel: (value: boolean) => void;
};

const CreativesReport = memo<Props>((props) => {
  const {
    isChatOpen,
    setIsChatOpen,
    reportConfig,
    setReportConfig,
    reportConfigLoading,
    selectedSection,
    setSelectedSection,
    handleAddSection,
    allowedDateRange,
    isAccountSynced,
    selectedSidebarItem,
    isCreatingLabel,
    setIsCreatingLabel,
  } = props;

  // Use selectors to subscribe only to needed state
  const userName = useGlobalStore((state) => state.userName);
  const selectedCreativeReport = useGlobalStore((state) => state.selectedCreativeReport);
  const isCreativeListLoading = useGlobalStore((state) => state.isCreativeListLoading);
  const dataConfigurations = useGlobalStore((state) => state.dataConfigurations);
  const setDataConfigurations = useGlobalStore((state) => state.setDataConfigurations);
  const isAutoSaving = useGlobalStore((state) => state.isAutoSaving);
  const setIsAutoSaving = useGlobalStore((state) => state.setIsAutoSaving);
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const creativeReportList = useGlobalStore((state) => state.creativeReportList);

  const { accountId } = useParams();
  const [timeouts, setTimeouts] = useState<{
    [key: string]: NodeJS.Timeout;
  }>({});
  const [isReportSyncing, setIsReportSyncing] = useState<boolean>(false);
  const [isSectionRetrying, setIsSectionRetrying] = useState<boolean>(false);
  const [showNewDateNotification, setShowNewDateNotification] = useState<boolean>(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  // Custom hooks
  // Refs for section components
  const sectionComponentRefs = useRef<{
    [key: string]: CreativeReportSectionRef | null;
  }>({});

  // Create a stable ref callback factory to prevent unnecessary re-renders
  // We memoize the callbacks per section ID to ensure stable references
  const sectionRefCallbacks = useRef<{
    [sectionId: string]: (el: CreativeReportSectionRef | null) => void;
  }>({});

  const getSectionRefCallback = useCallback((sectionId: string) => {
    if (!sectionRefCallbacks.current[sectionId]) {
      sectionRefCallbacks.current[sectionId] = (el: CreativeReportSectionRef | null) => {
        sectionComponentRefs.current[sectionId] = el;
      };
    }
    return sectionRefCallbacks.current[sectionId];
  }, []);
  const {
    checkCache,
    saveToCache,
    clearCacheForSection,
    clearCacheByConfiguration,
    updateCacheConfiguration,
  } = useReportCache();

  // Handle section sync completion
  const handleSectionSyncComplete = useCallback(
    (updatedSection: CreativeReportSection) => {
      if (updatedSection.last_synced_at) {
        setLastSyncedAt(updatedSection.last_synced_at);
      }
    },
    [],
  );

  // Handle section config loaded (section fetches its own config by ID)
  const handleSectionConfigLoaded = useCallback((section: CreativeReportSection) => {
    if (section.last_synced_at) {
      setLastSyncedAt(section.last_synced_at);
    }
  }, []);

  const handleNewDateAvailable = useCallback(() => {
    setShowNewDateNotification(true);
  }, []);

  const handleDismissNewDateNotification = useCallback(() => {
    setShowNewDateNotification(false);
  }, []);

  const handleSectionRetryStart = useCallback(() => {
    setIsSectionRetrying(true);
  }, []);

  const handleSectionRetryComplete = useCallback(() => {
    setIsSectionRetrying(false);
  }, []);

  const handleFatigueSettingsSaved = useCallback(() => {
    // Trigger refetch for all sections
    Object.values(sectionComponentRefs.current).forEach((ref) => {
      if (ref) {
        const config = ref.getSectionConfiguration();
        void ref.fetchSectionData(ref.getSectionId(), config, false);
      }
    });
  }, []);

  const {
    isOpen: fatigueSettingsOpen,
    setIsOpen: setFatigueSettingsOpen,
    comparisonPeriod,
    setComparisonPeriod,
    conditions: fatigueConditions,
    setConditions: setFatigueConditions,
    handleSave: handleSaveFatigueSettings,
    resetToLastSaved,
  } = useFatigueSettings(
    selectedAccount?.id,
    selectedAccount?.additional_info?.fatigue_settings,
    handleFatigueSettingsSaved,
  );

  const [searchParams] = useSearchParams();

  // Add an isMounted ref to track component lifecycle
  const isMounted = useRef(true);

  // Add a ref to track the last configuration for each section
  const lastConfigurationRef = useRef<{
    [sectionId: string]: CreativeSectionConfiguration;
  } | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    // Reset the last fetched section ID when report changes
    // Auto-select first section when report changes if no section is selected
    if (reportConfig?.sections && reportConfig.sections.length > 0 && !selectedSection) {
      setSelectedSection(reportConfig.sections[0]);
    } else {
      setSelectedSection(null);
    }
    Object.values(timeouts).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    setTimeouts({});
    lastConfigurationRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreativeReport?.id, accountId]);

  // Section component fetches its own configuration by ID - no need to fetch here

  const debouncedUpdateSection = useMemo(
    () =>
      debounce((sectionId: string, data: { title?: string; icon?: string }) => {
        if (accountId && selectedCreativeReport?.id) {
          setIsAutoSaving(true);
          void updateReportSection(
            accountId,
            selectedCreativeReport.id,
            sectionId,
            data,
          ).finally(() => {
            setIsAutoSaving(false);
          });
        }
      }, 1000),
    [accountId, selectedCreativeReport?.id, setIsAutoSaving],
  );

  const debouncedUpdateConfiguration = useMemo(
    () =>
      debounce((sectionId: string, configuration: CreativeSectionConfiguration) => {
        if (accountId && selectedCreativeReport?.id) {
          setIsAutoSaving(true);
          void updateReportSection(accountId, selectedCreativeReport.id, sectionId, {
            configuration,
          }).finally(() => {
            setIsAutoSaving(false);
          });
        }
      }, 1000),
    [accountId, selectedCreativeReport?.id, setIsAutoSaving],
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => debouncedUpdateConfiguration.cancel();
  }, [debouncedUpdateConfiguration]);

  const handleTitleChange = useCallback(
    (
      sectionId: string,
      data: { title?: string; icon?: string },
      changedByUser?: boolean,
    ) => {
      const group_choices =
        dataConfigurations?.dropdowns
          ?.find((d) => d.key === "group_by")
          ?.options?.map((option) => option.label.trim()) || [];
      const current_title =
        reportConfig?.sections.find((s) => s.id === sectionId)?.title || "";

      // Only update the section title if one of these conditions is true:
      // 1. The section is currently "Untitled Section" (default title)
      // 2. The title change was explicitly made by the user
      // 3. The current title matches one of the group choices (system-generated title)
      // This prevents overwriting custom titles that users have set
      if (
        current_title.trim() !== "Untitled Section" &&
        !changedByUser &&
        !group_choices?.includes(current_title.trim())
      ) {
        // Skip the update if the title appears to be custom
        return;
      }

      setReportConfig((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          sections: prev.sections.map((section) =>
            section.id === sectionId ? { ...section, ...data } : section,
          ),
        };
      });

      // Also update selectedSection if this is the currently selected section
      if (selectedSection?.id === sectionId) {
        setSelectedSection({
          ...selectedSection,
          ...data,
        });
      }

      debouncedUpdateSection(sectionId, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      reportConfig?.sections,
      debouncedUpdateSection,
      dataConfigurations?.dropdowns,
      selectedSection,
      setSelectedSection,
    ],
  );

  // Add event listener for label updates
  useEffect(() => {
    const handleLabelUpdate = async (event: CustomEvent<{ labelConfigId: string }>) => {
      const { labelConfigId } = event.detail;

      // Clear cache for all sections across all reports that use the updated label
      if (accountId) {
        try {
          await clearCacheByConfiguration(accountId, (config) => {
            return config.dropdowns?.["group_by"] === labelConfigId;
          });
        } catch (error) {
          console.error(
            "Error clearing cache for updated label:",
            error instanceof Error ? error.message : String(error),
          );
        }
      }

      // Trigger refetch for sections that use this label
      Object.values(sectionComponentRefs.current).forEach((ref) => {
        if (ref) {
          const config = ref.getSectionConfiguration();
          if (config.dropdowns?.["group_by"] === labelConfigId) {
            void ref.fetchSectionData(ref.getSectionId(), config, true);
          }
        }
      });
    };

    window.addEventListener(
      "labelUpdated",
      handleLabelUpdate as unknown as EventListener,
    );
    return () => {
      window.removeEventListener(
        "labelUpdated",
        handleLabelUpdate as unknown as EventListener,
      );
    };
  }, [accountId, clearCacheByConfiguration]);

  const removeSectionOnDeletingLabel = useCallback(
    (labelId: string) => {
      if (!reportConfig) return;

      // Find ALL sections that use this label by checking section configuration
      // directly from reportConfig (not from component refs, since only the
      // currently rendered section has a ref).
      // Also check component refs for the active section whose config may have
      // been updated locally without persisting back to reportConfig yet.
      const sectionsToRemove = new Set<string>();
      reportConfig.sections.forEach((section) => {
        if (section.configuration?.dropdowns?.group_by === labelId) {
          sectionsToRemove.add(section.id);
        }
      });
      // Additionally check refs for any locally-modified config
      Object.values(sectionComponentRefs.current).forEach((ref) => {
        if (ref) {
          const config = ref.getSectionConfiguration();
          if (config.dropdowns?.["group_by"] === labelId) {
            sectionsToRemove.add(ref.getSectionId());
          }
        }
      });

      const updatedSections = reportConfig.sections.filter(
        (section) => !sectionsToRemove.has(section.id),
      );

      if (
        selectedSection?.id &&
        !updatedSections.some((section) => section.id === selectedSection.id)
      ) {
        if (updatedSections.length > 0) {
          setSelectedSection(updatedSections[0]);
        } else {
          setSelectedSection(null);
        }
      }
      // Clean up refs for removed sections to prevent stale ref errors
      sectionsToRemove.forEach((id) => {
        delete sectionComponentRefs.current[id];
        delete sectionRefCallbacks.current[id];
      });

      // Remove sections from reportConfig
      setReportConfig((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          sections: updatedSections,
        };
      });
    },
    [reportConfig, setReportConfig, selectedSection?.id, setSelectedSection],
  );

  const onCreateLabel = useCallback(
    async (labelId: string, labelTitle?: string) => {
      if (!accountId) return;

      // Refresh data configurations first so the new label appears in
      // group_by dropdowns when the section renders
      const latestConfiguration: DataConfigurations = (
        await getDataConfigurations(accountId)
      ).configuration;
      setDataConfigurations(latestConfiguration);

      const shouldApply = searchParams.get("apply") === "true";
      if (shouldApply) {
        // Resolve the display title: use the passed title or look it up from config
        const title =
          labelTitle ||
          latestConfiguration.dropdowns
            ?.find((d) => d.key === "group_by")
            ?.options?.find((choice) => choice.key === labelId)?.label;
        if (!title) return;

        // Turn off the glow animation before the new section renders
        setIsCreatingLabel(false);

        void handleAddSection({ dropdowns: { group_by: labelId } }, title);
      } else {
        setIsCreatingLabel(false);
      }
    },
    [accountId, setDataConfigurations, searchParams, handleAddSection],
  );
  const onEditLabel = useCallback(
    async (labelId: string, isTitleOnlyChange?: boolean) => {
      if (!accountId) return;
      // Update data configurations
      const latestConfiguration: DataConfigurations = (
        await getDataConfigurations(accountId)
      ).configuration;
      setDataConfigurations(latestConfiguration);
      const updatedGroupBy = latestConfiguration.dropdowns
        ?.find((d) => d.key === "group_by")
        ?.options?.find((choice) => choice.key === labelId);

      // Find sections that use this label
      const staleSections: string[] = [];
      Object.values(sectionComponentRefs.current).forEach((ref) => {
        if (ref) {
          const config = ref.getSectionConfiguration();
          if (config.dropdowns?.["group_by"] === labelId) {
            staleSections.push(ref.getSectionId());
          }
        }
      });

      // Update titles for stale sections
      const title: string = updatedGroupBy?.label ?? "";
      if (title) staleSections.forEach((secId) => handleTitleChange(secId, { title }));

      // If only the title changed, no need to refetch - the title update is handled above
      if (isTitleOnlyChange) {
        return;
      }

      // Clear cache for all sections across all reports that use the updated label
      try {
        await clearCacheByConfiguration(accountId, (config) => {
          return config.dropdowns?.["group_by"] === labelId;
        });
      } catch (error) {
        console.error(
          "Error clearing cache for sections using updated label:",
          error instanceof Error ? error.message : String(error),
        );
      }

      // Trigger refetch for affected sections
      staleSections.forEach((secId) => {
        const sectionRef = sectionComponentRefs.current[secId];
        if (sectionRef) {
          const currentConfig = sectionRef.getSectionConfiguration();
          void sectionRef.fetchSectionData(secId, currentConfig, false);
        }
      });
    },
    [accountId, setDataConfigurations, handleTitleChange, clearCacheByConfiguration],
  );
  const handleSyncReport = useCallback(async () => {
    if (!selectedSection?.id) {
      toast.message(
        <ToastContent
          headingPrefix="An error occurred!"
          subheadingSuffix="Unable to sync section. No section selected."
          isError={true}
        />,
      );
      return;
    }

    setIsReportSyncing(true);
    try {
      const sectionRef = sectionComponentRefs.current[selectedSection.id];
      if (sectionRef) {
        await sectionRef.syncSection();
      } else {
        toast.message(
          <ToastContent
            headingPrefix="An error occurred!"
            subheadingSuffix="Unable to sync section. Section reference not found."
            isError={true}
          />,
        );
      }
    } catch (error) {
      console.error("Error syncing section:", error);
    } finally {
      setIsReportSyncing(false);
    }
  }, [selectedSection?.id]);

  if (isCreativeListLoading || reportConfigLoading)
    return (
      <div className="w-full h-full flex justify-center items-center">
        <PageLoader />
      </div>
    );

  const regularReport = creativeReportList?.find(
    (r) => r.type === ReportType.Regular && r.sync_status === "success",
  );

  const fatigueReport = creativeReportList?.find(
    (r) => r.type === ReportType.Fatigue && r.sync_status === "success",
  );

  if (selectedSidebarItem?.id === "regular" && !isCreativeListLoading && !regularReport)
    return <NoReports allowedDateRange={allowedDateRange ?? undefined} />;

  if (selectedSidebarItem?.id === "fatigue" && !isCreativeListLoading && !fatigueReport)
    return <NoReports allowedDateRange={allowedDateRange ?? undefined} />;

  if (!reportConfig) return null;
  return (
    <div className="relative flex flex-col w-full h-full pt-[8px] bg-background-gray">
      {/* AI gradient glow around the entire screen while creating a label */}
      <div
        className={`ai-glow-wrapper ai-glow--screen pointer-events-none fixed inset-0 ${isCreatingLabel ? "ai-glow-active" : ""}`}
        style={{ zIndex: 9999 }}
      >
        <div className="ai-glow-edge ai-glow-edge-top" />
        <div className="ai-glow-edge ai-glow-edge-right" />
        <div className="ai-glow-edge ai-glow-edge-bottom" />
        <div className="ai-glow-edge ai-glow-edge-left" />
      </div>
      {/*Button Section*/}
      <div className="absolute w-max top-2 right-0 z-[15] flex justify-between pl-[9px] pr-[14px]">
        <div className="flex justify-end gap-2 pb-2">
          {isAutoSaving && (
            <div className="flex items-center gap-[4px] mr-1">
              <LuLoaderCircle className="w-[14px] h-[14px] animate-spin flex-shrink-0 text-[#9AA3BD]" />
              <span className="mt-[2px] overflow-hidden text-[#9AA3BD] text-ellipsis text-[13px] font-normal leading-[19.157px] tracking-[0.26px]">
                Auto Saving...
              </span>
            </div>
          )}
          {reportConfig.sections.some(
            (s) => s.configuration.section_type === "fatigue",
          ) && (
            <Button
              variant="sq-outline"
              className="bg-white hover:bg-hover-gray shadow-sm flex items-center gap-[6px]"
              onClick={() => setFatigueSettingsOpen(true)}
            >
              <LuSettings2 className="text-brand-primary-400" size={14} />
              <span className="overflow-hidden text-brand-primary-800 text-ellipsis text-[11px] font-medium leading-[19.157px] tracking-[0.24px] relative top-[1px]">
                Fatigue Settings
              </span>
            </Button>
          )}
          <div className="flex flex-row gap-2">
            <div className="text-brand-primary-400 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] self-center whitespace-nowrap">
              {isReportSyncing || isSectionRetrying
                ? "Syncing..."
                : lastSyncedAt
                  ? `Synced ${formatRelativeTime(new Date(lastSyncedAt))}`
                  : ""}
            </div>
            <Button
              variant={"sq-outline"}
              onClick={() => void handleSyncReport()}
              disabled={isReportSyncing}
              className="h-8 bg-white hover:bg-hover-gray shadow-sm"
            >
              {isReportSyncing ? (
                <LoadingSpinner color="#FF4500" size={14} className="shrink-0" />
              ) : (
                <RiRefreshLine size={16} className="shrink-0 text-brand-primary-400" />
              )}
              <span className="mt-[2px] text-[12px] font-medium text-brand-primary-800">
                Refresh
              </span>
            </Button>
          </div>
          <TooltipProvider delayDuration={500}>
            <Tooltip>
              <TooltipTrigger
                className={`${isChatOpen || !selectedCreativeReport?.id ? "hidden" : ""}`}
              >
                <div
                  onClick={() =>
                    !isChatOpen && selectedCreativeReport?.id && setIsChatOpen(true)
                  }
                >
                  <div
                    className={`h-8 flex items-center gap-[6px] px-[14px] py-[6px] squircle-mask bg-gradient-to-r from-[#FF4500]/90 to-[#FF5188]/90 rounded-[8px] ${
                      isChatOpen || !selectedCreativeReport?.id
                        ? "cursor-not-allowed opacity-0 hidden"
                        : "cursor-pointer hover:text-accent-foreground"
                    }`}
                  >
                    <RiChatSmileAiLine size={16} className="flex-shrink-0 text-white" />
                    <span className="text-white text-[12px] font-medium tracking-[0.24px] relative top-[1px] whitespace-nowrap">
                      Ask AI
                    </span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent
                align="end"
                className="text-white text-[12px] py-2 px-3 min-w-0 max-w-[240px] h-7 squircle squircle-fill-[black]"
              >
                <span className="relative -top-[2px]">
                  Hello, {userName?.split(" ")?.[0] ?? "Stranger"}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* New date available (midnight rollover) notification */}
      {showNewDateNotification && (
        <div
          className="absolute top-[6px] left-1/2 -translate-x-1/2"
          style={{ zIndex: 1000 }}
        >
          <ToastNotification
            message="New date available."
            actionLabel="Refresh"
            onActionClick={() => window.location.reload()}
            onDismissClick={handleDismissNewDateNotification}
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {/* Only render the selected section */}
        {selectedSection && (
          <div className="w-full h-full">
            <div
              data-section-id={selectedSection.id}
              className="pl-[9px] pr-[14px] h-full"
            >
              <SectionComponent
                key={selectedSection.id}
                ref={getSectionRefCallback(selectedSection.id)}
                accountId={accountId}
                isAccountSynced={isAccountSynced}
                reportId={reportConfig?.id}
                initialSection={selectedSection}
                setIsAutoSaving={setIsAutoSaving}
                onTitleChange={handleTitleChange}
                removeSectionOnDeletingLabel={removeSectionOnDeletingLabel}
                reportType={reportConfig?.type ?? ReportType.Regular}
                checkCache={checkCache}
                saveToCache={saveToCache}
                updateCacheConfiguration={updateCacheConfiguration}
                allowedDateRange={allowedDateRange}
                timezone={selectedAccount?.time_zone}
                onSectionSyncComplete={handleSectionSyncComplete}
                onSectionConfigLoaded={handleSectionConfigLoaded}
                clearCacheForSection={clearCacheForSection}
                onNewDateAvailable={handleNewDateAvailable}
                onSectionRetryStart={handleSectionRetryStart}
                onSectionRetryComplete={handleSectionRetryComplete}
                configFresh={selectedSection.configFresh}
              />
            </div>
          </div>
        )}
      </div>

      <LabelDescriptionDialog
        onCreate={onCreateLabel}
        onEdit={onEditLabel}
        onSubmitStart={() => setIsCreatingLabel(true)}
        onSubmitError={() => setIsCreatingLabel(false)}
      />
      <FatigueSettingsDialog
        open={fatigueSettingsOpen}
        onOpenChange={setFatigueSettingsOpen}
        comparisonPeriod={comparisonPeriod}
        setComparisonPeriod={setComparisonPeriod}
        conditions={fatigueConditions}
        setConditions={setFatigueConditions}
        onSave={handleSaveFatigueSettings}
        metrics={
          dataConfigurations?.column_choices.filter(
            (column) => column.key === "metric",
          )[0]?.options ?? []
        }
        resetToLastSaved={resetToLastSaved}
      />
    </div>
  );
});

CreativesReport.displayName = "CreativesReport";

export default CreativesReport;
